import React, { useState, useEffect } from 'react';
import { 
    Box, Button, MenuItem, TextField, Typography, Alert, AlertTitle, Stack, CircularProgress, 
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton 
} from '@mui/material';
import { Send, ContentCopy, Close, LockReset } from '@mui/icons-material';
import { useValue } from '../../../context/ContextProvider';
// import { useUsersMutation } from '../../../hooks/mutations'; 
import { useAppStore } from '../../../store/appStore.tsx';
import { crudUsersApi } from '../../../actions/usersApi.js';
import { useQueryClient } from '@tanstack/react-query';

const ROLES_DISPONIBLES = [
    { value: 'usuario', label: 'Usuario (Solo uso)' },
    { value: 'tecnico', label: 'Técnico (Instalador)' },
    { value: 'administrador', label: 'Administrador' },
    { value: 'superusuario', label: 'Superusuario (Acceso Total)' }
];

const AddEditUsuario = ({ openDialog, setOpenDialog, isUpdate, selectedRow }) => {
    const { dispatch } = useValue();
    const currentUser = useAppStore(appStore => appStore.currentUser);
    const queryClient = useQueryClient(); // 2. Inicializa el queryClient
    // const usersMutation = useUsersMutation();

    const [formData, setFormData] = useState({
        name: '',
        username: '',
        email: '',
        celular: '',
        devId: '',
        telegramChatId: '',
        tipoUsuario: 'usuario'
    });

    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (openDialog) {
            setTempPassword('');
            setErrorMsg('');
            if (isUpdate && selectedRow) {
                setFormData({
                    name: selectedRow.name || '',
                    username: selectedRow.username || '',
                    email: selectedRow.email || '',
                    celular: selectedRow.celular || '',
                    devId: selectedRow.devId !== undefined ? selectedRow.devId : '',
                    telegramChatId: selectedRow.telegramChatId || '',
                    tipoUsuario: selectedRow.tipoUsuario || 'usuario'
                });
            } else {
                setFormData({ 
                    name: '', 
                    username: '', 
                    email: '', 
                    celular: '', 
                    devId: '', 
                    telegramChatId: '', 
                    tipoUsuario: 'usuario' 
                });
            }
        }
    }, [isUpdate, selectedRow, openDialog]);

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Validación estricta para username (máximo 16 caracteres)
        if (name === 'username' && value.length > 16) {
            return;
        }

        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setTempPassword('');
        setErrorMsg('');

        try {
            const comando = isUpdate ? "CMD_UPDATE_USER" : "CMD_CREATE_USER";
            if (!isUpdate) setTempPassword('h7x9k2p'); 
            const payload = isUpdate ? { ...formData, id: selectedRow.id, isUpdate } : {...formData, isUpdate};
            const body = { comando, token: currentUser?.token, data: payload };

            const resp = await crudUsersApi(body);
            //console.log(`[AddEditUsuario] Resp: ${JSON.stringify(resp)}`);
            if(resp.success === true){
                dispatch({ type: 'UPDATE_ALERT', payload: { open: true, severity: 'success', message: `Usuario ${isUpdate ? 'actualizado' : 'creado'} correctamente` } });
                await queryClient.invalidateQueries({ queryKey: ['users'] }); 
            } else {
                dispatch({ type: 'UPDATE_ALERT', payload: { open: true, severity: 'error', message: `Error al ${isUpdate ? 'actualizar' : 'crear'} el usuario` } });
            }
            
            if (isUpdate) {
                handleClose();
            } else {
                setTempPassword(resp.result.tempPassword);  // Si es nuevo, muestras su pass temporal
            }
        } catch (error) {
            setErrorMsg(error.message || "Ocurrió un error de conexión.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!window.confirm("¿Está seguro que desea resetear la contraseña de este usuario? Se le asignará una provisional.")) return;
        
        setResetLoading(true);
        setErrorMsg('');

        try {
            const comando = "CMD_RESET_PASSWORD";
            const payload = { userId: selectedRow.id};
            const body = { comando, token: currentUser?.token, data: payload };

            const resp = await crudUsersApi(body);
            //console.log(`[handleResetUsuario] Resp: ${JSON.stringify(resp)}`);
            if(resp.success === true){
                setTempPassword(resp.result.tempPassword);  // Si es nuevo, muestras su pass temporal
                await queryClient.invalidateQueries({ queryKey: ['users'] }); 
            } else {
                dispatch({ type: 'UPDATE_ALERT', payload: { open: true, severity: 'error', message: `Error al reiniciar la contraseña. Intente nuevamente.` } });
            }
        } catch (error) {
            setErrorMsg(error.message || "Ocurrió un error de conexión.");
        } finally {
            setLoading(false);
            setResetLoading(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(tempPassword);
        dispatch({ type: 'UPDATE_ALERT', payload: { open: true, severity: 'info', message: 'Contraseña copiada al portapapeles' } });
    };

    return (
        <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isUpdate ? 'Editar Usuario' : 'Registrar Nuevo Usuario'}
                <IconButton sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500' }} onClick={handleClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Box component="form" id="usuario-form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <Stack spacing={2.5}>
                        <TextField
                            required fullWidth size="small" variant="outlined"
                            id="name" name="name" label="Nombre Completo"
                            value={formData.name} onChange={handleChange}
                        />

                        <TextField
                            fullWidth size="small" variant="outlined"
                            id="username" name="username" label="Username (máx. 16 caracteres)"
                            value={formData.username} onChange={handleChange}
                            inputProps={{ maxLength: 16 }}
                            helperText={`${formData.username.length}/16 caracteres`}
                        />

                        <TextField
                            required fullWidth size="small" variant="outlined"
                            id="email" name="email" label="Correo Electrónico" type="email"
                            value={formData.email} onChange={handleChange}
                        />

                        <TextField
                            fullWidth size="small" variant="outlined"
                            id="celular" name="celular" label="Número de Celular"
                            placeholder="+59177550631"
                            value={formData.celular} onChange={handleChange}
                        />

                        <TextField
                            fullWidth size="small" variant="outlined"
                            id="devId" name="devId" label="Id Dispositivo (32 bits)" type="number"
                            value={formData.devId} onChange={handleChange}
                        />

                        <TextField
                            fullWidth size="small" variant="outlined"
                            id="telegramChatId" name="telegramChatId" label="Telegram Chat Id" type="number"
                            value={formData.telegramChatId} onChange={handleChange}
                        />

                        <TextField
                            required fullWidth select size="small" variant="outlined"
                            id="tipoUsuario" name="tipoUsuario" label="Rol del Usuario"
                            value={formData.tipoUsuario} onChange={handleChange}
                        >
                            {ROLES_DISPONIBLES.map((option) => (
                                <MenuItem 
                                    key={option.value} value={option.value}
                                    disabled={option.value === 'superusuario' && currentUser.tipoUsuario !== 'superusuario'}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        {errorMsg && <Alert severity="error" onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

                        {tempPassword && (
                            <Alert severity="success" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                                <AlertTitle>{isUpdate ? 'Contraseña Restablecida' : 'Usuario Registrado'}</AlertTitle>
                                La contraseña temporal asignada es:
                                <Box sx={{ 
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                                    mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1, border: '1px dashed green' 
                                }}>
                                    <Typography variant="h6" sx={{ letterSpacing: 2, fontWeight: 'bold' }}>{tempPassword}</Typography>
                                    <Button size="small" color="success" startIcon={<ContentCopy />} onClick={handleCopyPassword}>Copiar</Button>
                                </Box>
                                <Typography variant="caption" display="block" mt={1}>
                                    Entregue esta contraseña al usuario. El sistema le obligará a cambiarla en su primer inicio de sesión.
                                </Typography>
                            </Alert>
                        )}
                    </Stack>
                </Box>

                {isUpdate && !selectedRow?.google && (
                    <Box mt={3} pt={2} borderTop="1px solid #eee">
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Seguridad de la cuenta
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="warning" 
                            startIcon={resetLoading ? <CircularProgress size={20} /> : <LockReset />}
                            onClick={handleResetPassword}
                            disabled={resetLoading}
                            fullWidth
                        >
                            {resetLoading ? 'Generando...' : 'Asignar nueva contraseña provisional'}
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ pr: 3, pt: 2, pb: 2 }}>
                <Button variant='outlined' onClick={handleClose} color="inherit">
                    Cancelar
                </Button>
                <Button 
                    type="submit" 
                    form="usuario-form" 
                    variant="contained" 
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                    disabled={loading}
                >
                    {loading ? 'Guardando...' : (isUpdate ? 'Actualizar Datos' : 'Registrar')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditUsuario;